import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}


/* ===== Boveme final seamless announcement marquee ===== */

function removeBovemeIds(node) {
  if (node.id) {
    node.removeAttribute('id');
  }

  node.querySelectorAll('[id]').forEach((element) => {
    element.removeAttribute('id');
  });
}

function normalizeBovemeSlides(node) {
  if (
    node.matches &&
    node.matches('.announcement-bar__slide[aria-hidden="true"]')
  ) {
    node.removeAttribute('aria-hidden');
  }

  node
    .querySelectorAll('.announcement-bar__slide[aria-hidden="true"]')
    .forEach((slide) => {
      slide.removeAttribute('aria-hidden');
    });
}

function initBovemeMarquee(root = document) {
  const marquees = root.querySelectorAll('.boveme-marquee');

  marquees.forEach((marquee) => {
    const track = marquee.querySelector('.boveme-marquee__track');

    if (!track) return;

    const originalGroup = track.querySelector(
      '.boveme-marquee__group:not([data-boveme-clone="true"])'
    );

    if (!originalGroup) return;

    /* 删除上一次生成的第二组 */
    track
      .querySelectorAll(
        '.boveme-marquee__group[data-boveme-clone="true"]'
      )
      .forEach((clone) => clone.remove());

    /* 删除上一次在第一组内部生成的重复项 */
    originalGroup
      .querySelectorAll('[data-boveme-repeat="true"]')
      .forEach((repeat) => repeat.remove());

    /* 清除前面旧代码留下的宽度 */
    originalGroup.style.removeProperty('width');
    originalGroup.style.removeProperty('min-width');
    originalGroup.style.removeProperty('flex-basis');

    const originalItems = Array.from(originalGroup.children);

    if (!originalItems.length) return;

    originalItems.forEach((item) => {
      normalizeBovemeSlides(item);
    });

    const viewportWidth =
      marquee.getBoundingClientRect().width || window.innerWidth;

    /*
     * 先在第一组内部重复公告，
     * 直到这一组本身已经超过整个屏幕宽度。
     */
    let safety = 0;

    while (
      originalGroup.scrollWidth < viewportWidth * 1.25 &&
      safety < 20
    ) {
      originalItems.forEach((originalItem) => {
        const repeat = originalItem.cloneNode(true);

        repeat.dataset.bovemeRepeat = 'true';

        removeBovemeIds(repeat);
        normalizeBovemeSlides(repeat);

        originalGroup.appendChild(repeat);
      });

      safety += 1;
    }

    /*
     * 再复制整个完整第一组。
     * Track 永远只有两个完全相同的大组。
     */
    const secondGroup = originalGroup.cloneNode(true);

    secondGroup.dataset.bovemeClone = 'true';

    removeBovemeIds(secondGroup);
    normalizeBovemeSlides(secondGroup);

    track.appendChild(secondGroup);

    const groupWidth =
      originalGroup.getBoundingClientRect().width;

    if (!groupWidth) return;

    track.style.setProperty(
      '--boveme-final-distance',
      `${groupWidth}px`
    );

    /*
     * 24px/s：慢速、稳定。
     * 同时最低保持 60 秒一圈。
     */
    const duration = 30;

    track.style.setProperty(
      '--boveme-final-duration',
      `${duration}s`
    );
  });
}

function startBovemeMarquee() {
  requestAnimationFrame(() => {
    initBovemeMarquee(document);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    startBovemeMarquee
  );
} else {
  startBovemeMarquee();
}

/* Shopify 编辑器重新载入公告栏时重新计算 */
document.addEventListener(
  'shopify:section:load',
  (event) => {
    requestAnimationFrame(() => {
      initBovemeMarquee(event.target);
    });
  }
);

/* 浏览器宽度变化时重新铺满 */
let bovemeResizeTimer;

window.addEventListener('resize', () => {
  clearTimeout(bovemeResizeTimer);

  bovemeResizeTimer = setTimeout(() => {
    initBovemeMarquee(document);
  }, 250);
});